import React, { ReactNode, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { ActiveSource, Source } from 'types/GlobalSource'

import { OptionProps } from 'components_new/molecules/OptionsMenu'

import EditTitleDialog from 'components_new/organisms/dialogs/EditTitleDialog'
import DeleteSourceDialog from 'components_new/organisms/internal/sources/DeleteSourceDialog'
import EditAuthDialog from 'components_new/organisms/internal/sources/EditAuthDialog'
import EditPaginationDialog from 'components_new/organisms/internal/sources/EditPaginationDialog'

import { useActiveSourcesActions } from 'redux/actions/DataPlatform/connections/ActiveSources'

const SCHEMA_FILE_TYPES = '.graphql,.gql,.graphqls,.txt'

/**
 * The actions in the topbar menu, together with the dialogs they open. Fabrics
 * GraphQL sources have no schema to scan — their schema is uploaded by hand.
 */
export const useSourceOptions = (
  activeSource: ActiveSource | null,
  source: Source | null
): { options: OptionProps[] | undefined; dialogs: ReactNode } => {
  const history = useHistory()
  const {
    tryActivateActiveSource,
    tryDeleteActiveSource,
    tryScanActiveSource,
    tryUpdateActiveSource
  } = useActiveSourcesActions()

  const fileInputRef = useRef<HTMLInputElement>(null)

  const [deleteOpen, setDeleteOpen] = useState(false)
  const [editAuthOpen, setEditAuthOpen] = useState(false)
  const [editNameOpen, setEditNameOpen] = useState(false)
  const [editPaginationOpen, setEditPaginationOpen] = useState(false)

  if (!activeSource || !source) {
    return { dialogs: null, options: undefined }
  }

  const isGraphql = source.attributes.is_fabrics_graphql
  const isHidden = source.attributes.type === 'HIDDEN'
  const isKpiApi = source.attributes.type === 'KPI_API'

  const blockedBy = (activeSource.included.active_shapes ?? []).map(
    (shape) => shape.attributes.name
  )

  const handleSchemaFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]

    // Reset so re-selecting the same file still fires onChange.
    event.target.value = ''

    if (file) {
      tryScanActiveSource(activeSource.id, file)
    }
  }

  const options: OptionProps[] = [
    {
      divider: !isHidden,
      iconName: 'Edit',
      onClick: () => setEditNameOpen(true),
      title: 'Redigera namn'
    },
    ...(isHidden
      ? [
          {
            iconName: 'VpnKeyOutlined' as const,
            onClick: () => setEditAuthOpen(true),
            title: 'Redigera autentisering'
          },
          {
            divider: true,
            iconName: 'TuneOutlined' as const,
            onClick: () => setEditPaginationOpen(true),
            title: 'Redigera pagination'
          }
        ]
      : []),
    {
      disabled: Boolean(activeSource.attributes.technical_name) || isKpiApi,
      iconName: 'PlayCircleFilledWhite',
      onClick: () => tryActivateActiveSource(activeSource.id),
      title: 'Aktivera datakälla'
    },
    {
      disabled:
        source.attributes.type !== 'API' ||
        source.attributes.is_azure_data_tables,
      divider: true,
      iconName: isGraphql ? 'CloudUploadOutlined' : 'Sync',
      onClick: () =>
        isGraphql
          ? fileInputRef.current?.click()
          : tryScanActiveSource(activeSource.id),
      title: isGraphql ? 'Ladda upp schema' : 'Skanna schema'
    },
    {
      color: 'error',
      iconName: 'Delete',
      onClick: () => setDeleteOpen(true),
      title: 'Ta bort datakälla'
    }
  ]

  const dialogs = (
    <>
      <input
        accept={SCHEMA_FILE_TYPES}
        onChange={handleSchemaFileChange}
        ref={fileInputRef}
        style={{ display: 'none' }}
        type="file"
      />

      <EditTitleDialog
        onClose={() => setEditNameOpen(false)}
        onSubmit={(displayName) =>
          tryUpdateActiveSource(activeSource.id, {
            data: { attributes: { display_name: displayName } }
          })
        }
        open={editNameOpen}
        title={activeSource.attributes.display_name}
      />

      {isHidden ? (
        <>
          <EditAuthDialog
            onClose={() => setEditAuthOpen(false)}
            onSubmit={(attributes) =>
              tryUpdateActiveSource(activeSource.id, { data: { attributes } })
            }
            open={editAuthOpen}
            source={source}
          />

          <EditPaginationDialog
            onClose={() => setEditPaginationOpen(false)}
            onSubmit={(pagination) =>
              tryUpdateActiveSource(activeSource.id, {
                data: { attributes: { pagination } }
              })
            }
            open={editPaginationOpen}
            source={source}
          />
        </>
      ) : null}

      <DeleteSourceDialog
        blockedBy={blockedBy}
        onClose={() => setDeleteOpen(false)}
        onDelete={() => {
          tryDeleteActiveSource(source.id, activeSource.id)
          history.push('/data-platform/collect/sources')
        }}
        open={deleteOpen}
      />
    </>
  )

  return { dialogs, options }
}
